/*
 * Tiny dependency-free SVG charts. Just enough for the analytics page —
 * deliberately not a charting library. If we ever need rich axes / tooltips
 * we'll bring in Recharts; until then, fewer deps is more important than
 * fancy features.
 */
import type { ReactNode } from "react";

const COLORS = {
  primary: "#2563eb",
  positive: "#16a34a",
  warn: "#d97706",
  danger: "#dc2626",
  muted: "#64748b",
  grid: "#e2e8f0",
};

function nice(n: number): number {
  if (n <= 0) return 1;
  const exp = Math.pow(10, Math.floor(Math.log10(n)));
  const m = n / exp;
  if (m <= 1) return 1 * exp;
  if (m <= 2) return 2 * exp;
  if (m <= 5) return 5 * exp;
  return 10 * exp;
}

export function ChartFrame({ title, sub, children }: {
  title?: string;
  sub?: string;
  children: ReactNode;
}) {
  return (
    <div>
      {title ? (
        <div className="mb-2">
          <div className="text-sm font-medium">{title}</div>
          {sub ? <div className="text-xs text-[var(--fg-muted)]">{sub}</div> : null}
        </div>
      ) : null}
      <div className="rounded-md border border-[var(--border)] bg-[var(--bg-card)] p-3">
        {children}
      </div>
    </div>
  );
}

export interface BarPoint {
  label: string;
  value: number;
  hint?: string;
}

export function HorizontalBars({ points, valueLabel = "" }: {
  points: BarPoint[];
  valueLabel?: string;
}) {
  if (points.length === 0) return <Empty />;
  const max = nice(Math.max(...points.map((p) => p.value), 1));
  return (
    <div className="space-y-2">
      {points.map((p, i) => {
        const pct = (p.value / max) * 100;
        return (
          <div key={i} className="grid grid-cols-[10rem_1fr_5rem] gap-2 items-center text-sm">
            <div className="truncate text-zinc-700" title={p.label}>{p.label}</div>
            <div className="h-5 rounded bg-zinc-100 overflow-hidden">
              <div
                className="h-full rounded"
                style={{ width: `${pct}%`, backgroundColor: COLORS.primary }}
                title={p.hint ?? `${p.value}${valueLabel}`}
              />
            </div>
            <div className="tabular-nums text-right text-zinc-600">
              {p.value.toLocaleString()}{valueLabel}
            </div>
          </div>
        );
      })}
    </div>
  );
}

export interface LineSeries {
  label: string;
  color?: string;
  /** Each point is [x-index, y] — x can be a date string, the SVG laid out evenly. */
  points: { x: string; y: number }[];
}

export function MultiLineChart({ series, height = 160, yLabel }: {
  series: LineSeries[];
  height?: number;
  yLabel?: string;
}) {
  if (series.length === 0 || series.every((s) => s.points.length === 0)) return <Empty />;

  const allXs = Array.from(
    new Set(series.flatMap((s) => s.points.map((p) => p.x))),
  ).sort();
  const maxY = Math.max(
    1,
    ...series.flatMap((s) => s.points.map((p) => p.y)),
  );
  const niceMax = nice(maxY);

  // Indexable map for each series: x → y
  const map = series.map((s) => {
    const m = new Map<string, number>();
    for (const p of s.points) m.set(p.x, p.y);
    return m;
  });

  const w = 600;
  const h = height;
  const pad = { top: 8, right: 12, bottom: 22, left: 32 };
  const innerW = w - pad.left - pad.right;
  const innerH = h - pad.top - pad.bottom;
  const xStep = allXs.length > 1 ? innerW / (allXs.length - 1) : innerW;

  const yToPx = (y: number) => pad.top + innerH - (y / niceMax) * innerH;

  return (
    <div>
      <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-auto" role="img">
        {/* y gridlines */}
        {[0, 0.5, 1].map((f) => {
          const y = pad.top + innerH * (1 - f);
          return (
            <g key={f}>
              <line x1={pad.left} y1={y} x2={pad.left + innerW} y2={y}
                stroke={COLORS.grid} strokeWidth={1} />
              <text x={pad.left - 4} y={y + 3} textAnchor="end"
                className="text-[10px] fill-zinc-500">
                {Math.round(niceMax * f).toLocaleString()}
              </text>
            </g>
          );
        })}
        {/* x labels — first / mid / last */}
        {[0, Math.floor((allXs.length - 1) / 2), allXs.length - 1]
          .filter((i, idx, arr) => i >= 0 && arr.indexOf(i) === idx)
          .map((i) => (
            <text key={i} x={pad.left + i * xStep} y={h - 6}
              textAnchor={i === 0 ? "start" : i === allXs.length - 1 ? "end" : "middle"}
              className="text-[10px] fill-zinc-500">
              {allXs[i]}
            </text>
          ))}
        {/* lines */}
        {series.map((s, si) => {
          const color = s.color ?? Object.values(COLORS)[si % 6];
          const m = map[si];
          const path = allXs
            .map((x, i) => {
              const y = m?.get(x);
              if (y === undefined) return null;
              return `${i === 0 ? "M" : "L"} ${pad.left + i * xStep} ${yToPx(y)}`;
            })
            .filter((c) => c !== null)
            .join(" ");
          return <path key={si} d={path} fill="none" stroke={color} strokeWidth={2} />;
        })}
      </svg>
      <div className="flex flex-wrap gap-4 text-xs text-zinc-600 mt-2 px-1">
        {series.map((s, si) => {
          const color = s.color ?? Object.values(COLORS)[si % 6];
          return (
            <span key={si} className="inline-flex items-center gap-1.5">
              <span className="inline-block w-3 h-0.5 rounded" style={{ backgroundColor: color }} />
              {s.label}
            </span>
          );
        })}
        {yLabel ? <span className="ml-auto text-[var(--fg-muted)]">{yLabel}</span> : null}
      </div>
    </div>
  );
}

export function StackedBars({ rows, totalLabel }: {
  rows: { label: string; total: number; segments: { label: string; value: number; color: string }[] }[];
  totalLabel?: string;
}) {
  if (rows.length === 0) return <Empty />;
  const max = Math.max(1, ...rows.map((r) => r.total));
  return (
    <div className="space-y-2">
      {rows.map((r, i) => (
        <div key={i} className="grid grid-cols-[8rem_1fr_5rem] gap-2 items-center text-sm">
          <div className="truncate text-zinc-700" title={r.label}>{r.label}</div>
          <div className="h-5 rounded bg-zinc-100 overflow-hidden flex">
            {r.segments.map((seg, j) => {
              const pct = (seg.value / max) * 100;
              return (
                <div key={j} title={`${seg.label}: ${seg.value}`}
                  style={{ width: `${pct}%`, backgroundColor: seg.color }} />
              );
            })}
          </div>
          <div className="tabular-nums text-right text-zinc-600">
            {r.total.toLocaleString()}{totalLabel ? ` ${totalLabel}` : ""}
          </div>
        </div>
      ))}
    </div>
  );
}

function Empty() {
  return <div className="text-sm text-[var(--fg-muted)] py-6 text-center">No data in this window.</div>;
}

export const ChartColors = COLORS;
